import { Effect } from 'effect';
import { PDFDocument } from 'pdf-lib';

import { makeParserDependencyError, type ParserDependencyError } from '../parser-host';
import type { CanonicalBbox } from '../types';

/**
 * Crop a single-page PDF to a normalized region by rewriting its CropBox.
 * Input must already be split to one page (host `pagePdf` flow); the region
 * is 0-1 in the page's top-left coordinate space, converted to PDF y-up points.
 */
export const cropPdfPageToRegionEffect = Effect.fn('okrapdf/parser-sdk.cropPdfPageToRegion')(
  function* (bytes: Uint8Array, region: CanonicalBbox): Effect.fn.Return<Uint8Array, ParserDependencyError> {
    return yield* Effect.tryPromise({
      try: async () => {
        const document = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const page = document.getPage(0);
        const { width, height } = page.getSize();
        const x = region.x * width;
        const w = region.w * width;
        const h = region.h * height;
        const y = (1 - region.y - region.h) * height;
        page.setCropBox(x, y, w, h);
        return document.save();
      },
      catch: (cause) =>
        makeParserDependencyError(
          { service: 'page-source', operation: 'page-pdf', code: 'invalid_page', retryable: false },
          cause,
        ),
    });
  },
);
